import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
  X,
  FileCheck,
  CheckCircle,
  Clock,
  Server,
  Loader2,
  AlertCircle,
  Shield,
  FileText
} from 'lucide-react';
import { ApiNames } from '../../components/Common/ApiManagement/apiServices';
import { ModernButton } from '../ModernUI';
import './GenerateReportModal.css';

const GenerateReportModal = ({ isOpen, onClose, onReportGenerated }) => {
  const [urlId, setUrlId] = useState('');
  const [scanId, setScanId] = useState('');
  const [generating, setGenerating] = useState(false);
  const [step, setStep] = useState('input');
  const [availableScans, setAvailableScans] = useState([]);
  const [selectedScan, setSelectedScan] = useState('');
  const [generatedReport, setGeneratedReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen) fetchAvailableScans();
  }, [isOpen]);

  const fetchAvailableScans = async () => {
    try {
      setLoading(true);
      const response = await fetch(ApiNames.SCANS);

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setAvailableScans(data.scans || []);
    } catch (error) {
      console.error('Error fetching scans:', error);
      toast.error('Failed to fetch available scans');
      setAvailableScans([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!scanId.trim()) {
      toast.error('Please select a scan from the dropdown');
      return;
    }

    setGenerating(true);
    setStep('generating');

    try {
      const endpoint = ApiNames.GENERATE_REPORT_FROM_SCAN(scanId);

      const response = await fetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' } });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setGeneratedReport(data);
      setStep('success');

      setTimeout(() => {
        onReportGenerated(data.scan_id);
      }, 2000);
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error(error.message || 'Failed to generate report. Please try again.');
      setStep('input');
    } finally {
      setGenerating(false);
    }
  };

  const handleScanSelect = (scan) => {
    setSelectedScan(`${scan.url_name} (URL ID: ${scan.url_id}) - Scan: ${scan.scan_id.substring(0, 8)}...`);
    setUrlId(scan.url_id.toString());
    setScanId(scan.scan_id);
  };

  const resetForm = () => {
    setUrlId('');
    setScanId('');
    setSelectedScan('');
    setGeneratedReport(null);
    setStep('input');
    setGenerating(false);
    setAvailableScans([]);
    setLoading(true);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="generate-report-modal" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div className="modal-backdrop" onClick={handleClose} style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.6)', zIndex: 1 }}></div>
      <div className="modal-container" style={{ position: 'relative', zIndex: 100, width: '100%', maxWidth: '520px' }}>
        <div className="modal-wrapper" style={{ position: 'relative', zIndex: 100 }}>
          <div className="modal-content-box" style={{ position: 'relative', width: '100%', background: '#ffffff', borderRadius: '14px', boxShadow: '0 20px 60px -15px rgba(0, 0, 0, 0.3), 0 10px 30px -10px rgba(0, 0, 0, 0.2)', overflow: 'hidden', zIndex: 100, border: '1px solid rgba(0, 0, 0, 0.05)' }}>
            {/* Header */}
            <div className="modal-header" style={{ background: '#ffffff', borderBottom: '1px solid #e5e7eb' }}>
              <div className="modal-header-title">
                <FileText className="modal-title-icon" style={{ color: '#3b82f6' }} />
                <h3>Generate Compliance Report</h3>
              </div>
              <button
                type="button"
                onClick={handleClose}
                className="modal-close-btn"
              >
                <X style={{ width: '1.25rem', height: '1.25rem' }} />
              </button>
            </div>

            {/* Content */}
            {step === 'input' && (
              <form onSubmit={handleSubmit} className="modal-form" style={{ background: '#ffffff' }}>
                <div className="form-section">
                  <label className="form-label">
                    Select URL and Scan for Report Generation
                  </label>

                  {loading ? (
                    <div className="loading-container">
                      <Loader2 className="loading-spinner" />
                      <span className="loading-text">Loading available scans...</span>
                    </div>
                  ) : availableScans.length === 0 ? (
                    <div className="empty-state-box">
                      <AlertCircle className="empty-icon" />
                      <p>No scans available</p>
                    </div>
                  ) : (
                    <div className="select-section">
                      <select
                        value={selectedScan}
                        onChange={(e) => {
                          const selectedValue = e.target.value;
                          if (!selectedValue) return setSelectedScan('');
                          const scan = availableScans.find(
                            s => `${s.url_name} (URL ID: ${s.url_id}) - Scan: ${s.scan_id.substring(0, 8)}...` === selectedValue
                          );
                          if (scan) handleScanSelect(scan);
                        }}
                        className="scan-select"
                      >
                        <option value="">Select a URL and Scan...</option>
                        {availableScans.map(scan => (
                          <option key={scan.scan_id} value={`${scan.url_name} (URL ID: ${scan.url_id}) - Scan: ${scan.scan_id.substring(0, 8)}...`}>
                            {scan.url_name} (URL ID: {scan.url_id}) - Scan: {scan.scan_id.substring(0, 8)}... {scan.is_latest ? '(Latest)' : scan.has_report ? '(Has Report)' : '(New)'}
                          </option>
                        ))}
                      </select>

                      {selectedScan && (
                        <div className="info-box info-box-blue">
                          <h4>Selected Scan Details</h4>
                          <div className="info-items">
                            {(() => {
                              const scan = availableScans.find(s => s.url_id.toString() === urlId);
                              return scan ? (
                                <>
                                  <div className="info-item">
                                    <Server className="info-icon" />
                                    <span>URL: {scan.url_name} (ID: {scan.url_id})</span>
                                  </div>
                                  <div className="info-item">
                                    <FileCheck className="info-icon" />
                                    <span>Scan ID: {scan.scan_id}</span>
                                  </div>
                                  <div className="info-item">
                                    <CheckCircle className="info-icon" />
                                    <span>Status: {scan.has_report ? 'Has Report' : 'New Scan'}</span>
                                  </div>
                                </>
                              ) : null;
                            })()}
                          </div>
                        </div>
                      )}

                      {selectedScan && (
                        <div className="info-box info-box-green">
                          <h4>Ready to Generate Report</h4>
                          <p>
                            A new compliance report will be generated based on the selected scan data.
                            This will analyze the current compliance status and provide updated metrics.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="modal-actions">
                  <ModernButton
                    type="button"
                    variant="secondary"
                    onClick={handleClose}
                  >
                    Cancel
                  </ModernButton>
                  <ModernButton
                    type="submit"
                    variant="primary"
                    icon={FileCheck}
                    disabled={!selectedScan || generating}
                  >
                    Generate Report
                  </ModernButton>
                </div>
              </form>
            )}

            {step === 'generating' && (
              <div className="progress-section" style={{ background: '#ffffff' }}>
                <div className="progress-icon-wrapper">
                  <Loader2 className="progress-spinner" />
                </div>
                <h3 className="progress-title">Generating Report</h3>
                <p className="progress-description">
                  Processing compliance frameworks and analyzing scan data...
                </p>
                <div className="progress-steps">
                  <div className="progress-step progress-step-complete">
                    <CheckCircle className="step-icon" />
                    <span>USWDS Compliance Framework</span>
                  </div>
                  <div className="progress-step progress-step-complete">
                    <CheckCircle className="step-icon" />
                    <span>OMB M-15-13 HTTPS Framework</span>
                  </div>
                  <div className="progress-step progress-step-loading">
                    <Loader2 className="step-icon step-icon-spin" />
                    <span>Calculating overall metrics...</span>
                  </div>
                </div>
              </div>
            )}

            {step === 'success' && generatedReport && (
              <div className="success-section" style={{ background: '#ffffff' }}>
                <div className="success-icon-wrapper">
                  <CheckCircle className="success-icon" />
                </div>
                <h3 className="success-title">Report Generated Successfully!</h3>
                <div className="success-details">
                  <div className="detail-item">
                    <span className="detail-label">Report ID:</span>
                    <span className="detail-value">{generatedReport.report_id}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Scan ID:</span>
                    <span className="detail-value">{generatedReport.scan_id}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Frameworks:</span>
                    <span className="detail-value">{generatedReport.frameworks_processed}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Overall Compliance:</span>
                    <span className="detail-value">{generatedReport.overall_compliance}%</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GenerateReportModal;
